import type z from "zod";
import { schemas } from "../api/generated";
import { PROBLEM_TYPES } from "./problemTypes";

type ProblemDetails = z.infer<typeof schemas.ProblemDetails>;

export const getUploadFileCustomErrorMessage = (error: ProblemDetails) => {
  if (error.type === PROBLEM_TYPES.BAD_REQUEST) {
    if (error.title === "No File Uploaded") {
      return "No file was uploaded.";
    }
    if (error.title === "Invalid File Extension") {
      return "The file uploaded has an invalid file extension.";
    }
    if (error.title === "File Size Too Large") {
      return "The file uploaded exceeded 30 KB in size.";
    }
    if (error.title === "Invalid File Format") {
      return "The file uploaded was not recognized as a valid 7-Zip file.";
    }
  }
  return "Unexpected error";
};

export const getAssignUserCustomErrorMessage = (error: ProblemDetails) => {
  if (error.type === PROBLEM_TYPES.MISSING_BODY_PROPERTY) {
    if (error.title === "Missing Required Body Property") {
      return "User and File IDs are required";
    }
  }
  if (error.type === PROBLEM_TYPES.INVALID_BODY_PROPERTY_FORMAT) {
    if (error.title === "User ID is invalid") {
      return "User ID is invalid";
    }
  }
  if (error.type === PROBLEM_TYPES.NOT_FOUND) {
    if (error.title === "File not found") {
      return "File not found";
    } else if (error.title === "User not found") {
      return "User not found";
    }
  }
  return "Unknown error";
};

export const getDeleteUserCustomErrorMessage = (error: ProblemDetails) => {
  if (error.type === PROBLEM_TYPES.NOT_FOUND) {
    if (error.title === "User not found") {
      return "User not found. Please refresh the page and try again.";
    }
  }
  // Surface the backend's deliberate, actionable guard messages instead of
  // masking them as "unexpected": deleting your own account (403 forbidden)
  // or deleting the last remaining Admin (409 conflict).
  if (
    error.type === PROBLEM_TYPES.FORBIDDEN ||
    error.type === PROBLEM_TYPES.CONFLICT
  ) {
    return error.detail ?? error.title ?? "You cannot delete this account.";
  }
  const unexpectedErrorMessage =
    "An unexpected error occurred while deleting the user. Please try again later.";
  if (error.type === PROBLEM_TYPES.SERVER_ERROR) {
    return unexpectedErrorMessage;
  }
  return error.detail ?? unexpectedErrorMessage;
};

export const getDeleteFileCustomErrorMessage = (error: ProblemDetails) => {
  if (error.type === PROBLEM_TYPES.NOT_FOUND) {
    return "File not found. Please refresh the page and try again.";
  }
  return "An unexpected error occurred while deleting the file. Please try again later.";
};

export const getRegisterCustomErrorMessage = (error: ProblemDetails) => {
  if (error) {
    if (error.type === PROBLEM_TYPES.ALREADY_EXISTS) {
      if (error.title === "User Exists") {
        return "User already exists";
      }
    }
    if (error.type === PROBLEM_TYPES.SERVER_ERROR) {
      if (error.title === "Error registering new user") {
        return "Error registering new user";
      }
    }
    if (error.title === "Feature Disabled") {
      return "Registration of new administrator users has been temporarily disabled.";
    }
  }
  return "Something went wrong try again";
};

export function getLoginCustomErrorMessage(error: ProblemDetails) {
  if (error) {
    if (error.type === PROBLEM_TYPES.MISSING_BODY_PROPERTY) {
      if (error.title === "Invalid Body Property Format") {
        if (error.errors?.some((e) => e.pointer === "#/email")) {
          return "Email cannot be empty!";
        } else if (error?.errors?.some((e) => e.pointer === "#/password")) {
          return "Password cannot be empty!";
        }
      }
    } else if (error.type === PROBLEM_TYPES.INVALID_BODY_PROPERTY_FORMAT) {
      if (error.title === "Invalid Body Property Format") {
        if (error.errors?.some((e) => e.pointer === "#/email")) {
          return "A valid email address or username is required.";
        } else if (error?.errors?.some((e) => e.pointer === "#/password")) {
          return "A valid password is required.";
        }
      }
    } else if (error.type === PROBLEM_TYPES.UNAUTHORIZED) {
      if (error.title === "Invalid Credentials") {
        return "Invalid Credentials";
      }
    }
  }

  return "An error occurred during login.";
}

export function getUnassignFileCustomErrorMessage(error: ProblemDetails) {
  if (error) {
    if (error.type === PROBLEM_TYPES.INVALID_BODY_PROPERTY_FORMAT) {
      if (error.title === "Invalid Body Property Format") {
        if (error.errors?.some((e) => e.pointer === "#/fileName")) {
          return "Failed to rename file because the file name was invalid";
        }
      }
    } else if (error.type === PROBLEM_TYPES.NOT_FOUND) {
      if (error.title === "Invalid Body Property Format") {
        if (error.errors?.some((e) => e.pointer === "#/email")) {
          return "A valid email address or username is required.";
        } else if (error?.errors?.some((e) => e.pointer === "#/password")) {
          return "A valid password is required.";
        }
      }
    } else if (error.type === PROBLEM_TYPES.UNAUTHORIZED) {
      if (error.title === "Invalid Credentials") {
        return "Failed to authorize";
      }
    }
  }

  return "Failed to unassign file from user";
}

export function getUpdateFileCustomErrorMessage(error: ProblemDetails) {
  if (error) {
    if (error.type === PROBLEM_TYPES.INVALID_BODY_PROPERTY_FORMAT) {
      if (error.errors?.some((e) => e.pointer === "#/fileName")) {
        return "Failed to rename file because the file name was invalid";
      }
    } else if (error.type === PROBLEM_TYPES.NOT_FOUND) {
      return "Failed to update file because it could not be found";
    } else if (error.type === PROBLEM_TYPES.UNAUTHORIZED) {
      if (error.title === "Invalid Credentials") {
        return "Failed to authorize file update";
      }
    }
  }

  return "Failed to update file";
}
